import React from 'react';
import { X, Shield, Building, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ProfessionalHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfessionalHelpModal: React.FC<ProfessionalHelpModalProps> = ({
  isOpen,
  onClose
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Professional access</h2>
              <p className="text-sm text-gray-600 mt-1">Invitation-based access tied to the correct business workspace.</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close dialog"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Alert Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex gap-3 items-start">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-blue-900 mb-1">Why you can't self-register</div>
                <div className="text-sm text-blue-800">
                  Professional accounts must be linked to a specific business workspace. This protects customer data,
                  ensures the correct permissions (schedule, services, payouts), and prevents accounts from being created in the wrong business.
                </div>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-3 mb-6">
            <div className="flex gap-3 p-3 border border-gray-200 rounded-lg bg-white">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Get invited</h3>
                <p className="text-sm text-gray-600">
                  Ask the business owner/admin to invite you as a Professional. The invitation is sent to your email address.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-3 border border-gray-200 rounded-lg bg-white">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Create your account from the invite</h3>
                <p className="text-sm text-gray-600">
                  Open the invitation email and follow the secure link to set your password.
                  Use the invited email as your sign-in username. Example: <span className="font-mono text-xs bg-gray-100 border border-gray-300 rounded px-1.5 py-0.5">pro@example.com</span>.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-3 border border-gray-200 rounded-lg bg-white">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Sign in</h3>
                <p className="text-sm text-gray-600">
                  Return to this page and sign in using your invited email and password.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4 border-gray-200" />

          {/* If you work for yourself */}
          <div className="mb-4">
            <h3 className="text-base font-bold text-gray-900 mb-2">If you work for yourself</h3>
            <p className="text-sm text-gray-600 mb-2">
              If you are an independent professional (you manage your own services and bookings),
              we recommend creating a <strong>Business account</strong>.
            </p>
            <ul className="text-sm text-gray-600 mb-3 list-disc list-inside space-y-1">
              <li>Create your service menu and pricing</li>
              <li>Manage your calendar, customers, and bookings</li>
              <li>Enable payments and invoices for your business</li>
            </ul>
            <Link
              to="/business-registration/"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-900 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
            >
              <Building className="h-4 w-4" />
              Create a Business account
            </Link>
          </div>

          <hr className="my-4 border-gray-200" />

          {/* Common issues */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-2">Common issues</h3>
            <ul className="space-y-3">
              <li className="text-sm text-gray-600">
                <strong className="text-gray-900">I didn't receive an invitation.</strong><br />
                Ask the business owner/admin to resend it and confirm they used the correct email address.
              </li>
              <li className="text-sm text-gray-600">
                <strong className="text-gray-900">I used a different email than the invitation.</strong><br />
                Sign in with the invited email. If your email needs to change, the business owner/admin must update it and resend an invitation.
              </li>
              <li className="text-sm text-gray-600">
                <strong className="text-gray-900">I forgot my password.</strong><br />
                Use <em>Forgot your password</em> on the sign-in page to reset.
              </li>
            </ul>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
          >
            <X className="w-4 h-4" />
            Close
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-900 rounded-full hover:bg-blue-800 transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalHelpModal;

